"use client";

import * as React from "react";
import { SearchIcon, XIcon } from "lucide-react";

import { cn } from "../../lib/utils";

type MobileSearchFieldProps = {
  value: string;
  onValueChange: (value: string) => void;
  placeholder: string;
  /** Accessible name (defaults to the placeholder). */
  label?: string;
  clearLabel: string;
  /** When given, a "Cancel" button appears beside the field while it holds text; it clears and blurs. */
  cancelLabel?: string;
  onCancel?: () => void;
  /**
   * Keeps Cancel visible even while the field is empty: for a full search screen whose Cancel leaves the screen
   * (it then only calls `onCancel`, leaving the text alone).
   */
  persistentCancel?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  /**
   * For a search that runs on demand (inside a `<form>`): a submit button beside the field while it holds text,
   * so the search never depends on the keyboard's search key alone.
   */
  submitLabel?: string;
  className?: string;
};

const FIELD_CLASS_NAME = "bg-card flex h-11 min-w-0 flex-1 items-center gap-2 rounded-full px-4 transition-shadow";
const ICON_CLASS_NAME = "size-[1.125rem] shrink-0";

/**
 * The phone search field: a full-width field that turns green while it holds text, with a clear button inside
 * and an optional Cancel beside it. One component for every phone search (lists, the members overview) so they
 * look and behave the same.
 */
export const MobileSearchField = React.forwardRef<HTMLInputElement, MobileSearchFieldProps>(function MobileSearchField(
  {
    value,
    onValueChange,
    placeholder,
    label,
    clearLabel,
    cancelLabel,
    onCancel,
    persistentCancel = false,
    onKeyDown,
    submitLabel,
    className,
  },
  forwardedRef,
) {
  const innerRef = React.useRef<HTMLInputElement | null>(null);
  const setRefs = React.useCallback(
    (node: HTMLInputElement | null) => {
      innerRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );
  const active = value.trim().length > 0;

  return (
    <div className={cn("flex min-w-0 items-center gap-2.5", className)}>
      <label className={cn(FIELD_CLASS_NAME, active ? "ring-primary ring-2" : "ring-border/70 ring-1")}>
        <SearchIcon aria-hidden className={cn(ICON_CLASS_NAME, active ? "text-primary" : "text-muted-foreground")} />
        <input
          ref={setRefs}
          type='search'
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label={label ?? placeholder}
          enterKeyHint='search'
          className='text-foreground placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent text-[15px] font-semibold outline-none placeholder:font-medium [&::-webkit-search-cancel-button]:hidden'
        />
        {active ? (
          <button
            type='button'
            aria-label={clearLabel}
            onClick={() => {
              onValueChange("");
              innerRef.current?.focus();
            }}
            className='bg-muted-foreground/30 text-background inline-flex size-6 shrink-0 items-center justify-center rounded-full'
          >
            <XIcon aria-hidden className='size-3.5' strokeWidth={3} />
          </button>
        ) : null}
      </label>
      {active && submitLabel ? (
        <button type='submit' className='bg-primary text-primary-foreground h-9 shrink-0 rounded-full px-4 text-[15px] font-extrabold active:brightness-95'>
          {submitLabel}
        </button>
      ) : null}
      {cancelLabel && (active || persistentCancel) ? (
        <button
          type='button'
          onClick={() => {
            if (persistentCancel) {
              onCancel?.();
              return;
            }
            onValueChange("");
            innerRef.current?.blur();
            onCancel?.();
          }}
          className='text-primary shrink-0 px-1 py-2 text-[15px] font-extrabold'
        >
          {cancelLabel}
        </button>
      ) : null}
    </div>
  );
});

/** How long the stand-in field waits for the screen's own field to take focus (a lazy route can be slow). */
const KEYBOARD_HANDOFF_MS = 3000;

/**
 * iOS only raises the keyboard for a focus made inside the tap itself; the search screen focuses its field after
 * a route change, which iOS ignores. Focusing an invisible stand-in field during the tap raises the keyboard,
 * and the screen's field then takes the focus over with the keyboard still up. The stand-in removes itself
 * once anything else is focused, or after a short wait.
 */
function raiseKeyboardForNextField() {
  if (typeof document === "undefined") return;
  const standIn = document.createElement("input");
  standIn.setAttribute("aria-hidden", "true");
  standIn.tabIndex = -1;
  // 16px keeps iOS from zooming the page on focus.
  standIn.style.cssText = "position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;font-size:16px;border:0;padding:0;";
  document.body.appendChild(standIn);
  standIn.focus({ preventScroll: true });
  const remove = () => {
    document.removeEventListener("focusin", onFocusIn, true);
    window.clearTimeout(timer);
    standIn.remove();
  };
  const onFocusIn = (event: FocusEvent) => {
    if (event.target !== standIn) remove();
  };
  document.addEventListener("focusin", onFocusIn, true);
  const timer = window.setTimeout(remove, KEYBOARD_HANDOFF_MS);
}

/**
 * The phone search field as a door rather than an input: the same field, at rest, that opens a search screen
 * when tapped (the home page's search). The tap raises the keyboard for the screen it opens, so the search
 * field there is ready to type in.
 */
export function MobileSearchFieldTrigger({
  placeholder,
  label,
  onPress,
  className,
}: {
  placeholder: string;
  /** Accessible name (defaults to the placeholder). */
  label?: string;
  onPress: () => void;
  className?: string;
}) {
  return (
    <button
      type='button'
      aria-label={label ?? placeholder}
      onClick={() => {
        raiseKeyboardForNextField();
        onPress();
      }}
      className={cn(
        FIELD_CLASS_NAME,
        // flex-none: the field style grows along a row; set in a column (the home band) that growth squashed it.
        "ring-border/70 w-full flex-none text-left ring-1 active:bg-muted focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2",
        className,
      )}
    >
      <SearchIcon aria-hidden className={cn(ICON_CLASS_NAME, "text-muted-foreground")} />
      <span className='text-muted-foreground min-w-0 flex-1 truncate text-[15px] font-medium'>{placeholder}</span>
    </button>
  );
}
